import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { 
  Truck, 
  ArrowLeft,
  FileText,
  Download,
  Euro,
  Calendar,
  Package,
  LogOut,
  ChevronRight,
  Printer,
  Building2
} from "lucide-react";
import { toast } from "sonner";

export default function Invoices() {
  const navigate = useNavigate();
  const { user, token, logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [invoices, setInvoices] = useState([]);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [invoiceDetail, setInvoiceDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchInvoices();
  }, []);

  const fetchInvoices = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/invoices`, { headers });
      setInvoices(response.data);
    } catch (e) {
      console.error("Error fetching invoices:", e);
      toast.error("Fehler beim Laden der Rechnungen");
    } finally {
      setLoading(false);
    }
  };

  const openInvoiceDetail = async (invoice) => {
    setSelectedInvoice(invoice);
    setLoadingDetail(true);
    try {
      const response = await axios.get(`${API}/invoices/${invoice.id}`, { headers });
      setInvoiceDetail(response.data);
    } catch (e) {
      toast.error("Fehler beim Laden der Rechnungsdetails");
    } finally {
      setLoadingDetail(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadCSV = () => {
    if (!invoiceDetail) return;
    
    const lines = [
      ["Rechnungsübersicht", invoiceDetail.month_display],
      ["Kunde", invoiceDetail.customer?.name || ""],
      ["Firma", invoiceDetail.customer?.company || ""],
      [""],
      ["Sendungsnummer", "Datum", "Empfänger", "Stadt", "Packstücke", "Preis (€)"],
      ...invoiceDetail.orders.map(o => [
        o.order_number,
        o.date,
        o.receiver,
        o.receiver_city,
        o.packages,
        o.price.toFixed(2)
      ]),
      [""],
      ["Netto", "", "", "", "", invoiceDetail.net_total.toFixed(2)],
      ["MwSt. (19%)", "", "", "", "", invoiceDetail.tax_amount.toFixed(2)],
      ["Gesamt (Brutto)", "", "", "", "", invoiceDetail.total.toFixed(2)]
    ];
    
    const csv = lines.map(row => row.join(";")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `Rechnung_${invoiceDetail.month}.csv`;
    a.click();
    toast.success("CSV heruntergeladen");
  };

  const totalOpenAmount = invoices
    .filter(inv => inv.status === "OFFEN")
    .reduce((sum, inv) => sum + inv.total, 0);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-slate-900 text-white sticky top-0 z-50 print:hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                <Truck className="h-6 w-6 text-white" />
              </div>
              <div>
                <span className="font-bold text-lg">SonderCargo.de</span>
                <span className="text-xs text-slate-400 block">Medical, Weapons and much more</span>
              </div>
            </div>
            
            <nav className="hidden md:flex items-center gap-6">
              <Link to="/dashboard" className="text-slate-300 hover:text-white transition" data-testid="nav-dashboard">
                Dashboard
              </Link>
              <Link to="/invoices" className="text-white font-medium" data-testid="nav-invoices">
                Rechnungen
              </Link>
              <Link to="/support" className="text-slate-300 hover:text-white transition" data-testid="nav-support">
                Support
              </Link>
            </nav>
            
            <div className="flex items-center gap-4">
              <div className="text-right hidden sm:block">
                <p className="font-medium">{user?.name}</p>
                <p className="text-xs text-slate-400 uppercase">{user?.role}</p>
              </div>
              <Button variant="ghost" size="icon" className="text-slate-300 hover:text-white" onClick={logout} data-testid="logout-btn">
                <LogOut className="h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="h-1 bg-gradient-to-r from-blue-600 via-blue-500 to-blue-400 print:hidden" />

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Link to="/dashboard" className="inline-flex items-center text-slate-600 hover:text-slate-900 mb-6 print:hidden" data-testid="back-link">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Zurück zum Dashboard
        </Link>

        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900" data-testid="page-title">
              {user?.role === "business" ? "Rechnungsübersicht" : "Zahlungsübersicht"}
            </h1>
            <p className="text-slate-500">
              {user?.role === "business" 
                ? `Monatliche Abrechnungen für ${user?.company_name || user?.name}`
                : `Ihre bezahlten Sendungen - ${user?.name}`
              }
            </p>
          </div>
          {totalOpenAmount > 0 && (
            <Card className="border-0 shadow-sm bg-amber-50" data-testid="open-amount-card">
              <CardContent className="p-4">
                <p className="text-sm text-amber-700">Offener Betrag</p>
                <p className="text-2xl font-bold text-amber-900">{totalOpenAmount.toFixed(2)}€</p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          <Card className="border-0 shadow-sm" data-testid="stat-total-invoices">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Gesamt Rechnungen</p>
                  <p className="text-3xl font-bold text-slate-900">{invoices.length}</p>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <FileText className="h-6 w-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-0 shadow-sm" data-testid="stat-total-orders">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Gesamt Sendungen</p>
                  <p className="text-3xl font-bold text-slate-900">
                    {invoices.reduce((sum, inv) => sum + inv.order_count, 0)}
                  </p>
                </div>
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <Package className="h-6 w-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-0 shadow-sm" data-testid="stat-total-amount">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Gesamtbetrag (Netto)</p>
                  <p className="text-3xl font-bold text-slate-900">
                    {invoices.reduce((sum, inv) => sum + inv.total, 0).toFixed(2)}€
                  </p>
                </div>
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Euro className="h-6 w-6 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Invoices List */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="border-b">
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Monatliche Rechnungen
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y">
              {invoices.map(invoice => (
                <div 
                  key={invoice.id}
                  className="p-4 hover:bg-slate-50 transition cursor-pointer flex items-center justify-between"
                  onClick={() => openInvoiceDetail(invoice)}
                  data-testid={`invoice-${invoice.id}`}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-lg flex items-center justify-center bg-blue-100">
                      <FileText className="h-6 w-6 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900">{invoice.month_display}</p>
                      <p className="text-sm text-slate-500">{invoice.order_count} Sendung(en)</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="font-bold text-slate-900">{invoice.total.toFixed(2)}€</p>
                      <p className="text-xs text-slate-500">Nettobetrag</p>
                    </div>
                    <ChevronRight className="h-5 w-5 text-slate-400" />
                  </div>
                </div>
              ))}
              
              {invoices.length === 0 && (
                <div className="p-12 text-center">
                  <FileText className="h-16 w-16 mx-auto text-slate-300 mb-4" />
                  <h3 className="font-semibold text-slate-900 mb-2">Keine Rechnungen vorhanden</h3>
                  <p className="text-slate-500">Erstellen Sie Ihre erste Sendung, um Rechnungen zu generieren.</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </main>

      {/* Invoice Detail Modal */}
      <Dialog open={!!selectedInvoice} onOpenChange={() => { setSelectedInvoice(null); setInvoiceDetail(null); }}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="invoice-detail-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>Rechnung {selectedInvoice?.month_display}</span>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={handleDownloadCSV} data-testid="download-csv-btn">
                  <Download className="h-4 w-4 mr-2" />
                  CSV
                </Button>
                <Button variant="outline" size="sm" onClick={handlePrint} data-testid="print-btn">
                  <Printer className="h-4 w-4 mr-2" />
                  Drucken
                </Button>
              </div>
            </DialogTitle>
          </DialogHeader>
          
          {loadingDetail ? (
            <div className="py-12 flex justify-center">
              <div className="spinner" />
            </div>
          ) : invoiceDetail && (
            <div className="space-y-6" id="invoice-print-area">
              {/* Header */}
              <div className="flex justify-between items-start border-b pb-4">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                      <Truck className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <p className="font-bold">SonderCargo.de</p>
                      <p className="text-xs text-slate-500">Medical, Weapons and much more</p>
                    </div>
                  </div>
                  <p className="text-sm text-slate-500">Franz-Schubert-Str. 7, 84518 Garching an der Alz</p>
                </div>
              </div>
              
              {/* Customer Info */}
              <div className="bg-slate-50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Building2 className="h-4 w-4 text-slate-500" />
                  <span className="text-sm font-medium text-slate-500">Rechnungsempfänger</span>
                </div>
                <p className="font-semibold">{invoiceDetail.customer?.company || invoiceDetail.customer?.name}</p>
                <p className="text-sm text-slate-600">{invoiceDetail.customer?.address}</p>
                <p className="text-sm text-slate-600">{invoiceDetail.customer?.postal_code} {invoiceDetail.customer?.city}</p>
                <p className="text-sm text-slate-600">{invoiceDetail.customer?.email}</p>
              </div>
              
              {/* Orders Table */}
              <div>
                <h3 className="font-semibold mb-3">Leistungsübersicht {invoiceDetail.month_display}</h3>
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="p-3 text-left font-medium text-slate-500">Sendung</th>
                        <th className="p-3 text-left font-medium text-slate-500">Datum</th>
                        <th className="p-3 text-left font-medium text-slate-500">Empfänger</th>
                        <th className="p-3 text-center font-medium text-slate-500">Pakete</th>
                        <th className="p-3 text-right font-medium text-slate-500">Betrag</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoiceDetail.orders?.map((order, idx) => (
                        <tr key={idx} className="border-t">
                          <td className="p-3 font-medium">{order.order_number}</td>
                          <td className="p-3 text-slate-600">{order.date}</td>
                          <td className="p-3">
                            <p>{order.receiver}</p>
                            <p className="text-xs text-slate-500">{order.receiver_city}</p>
                          </td>
                          <td className="p-3 text-center">{order.packages}</td>
                          <td className="p-3 text-right font-medium">{order.price.toFixed(2)}€</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              
              {/* Totals */}
              <div className="border-t pt-4">
                <div className="flex justify-end">
                  <div className="w-64 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">Zwischensumme (Netto)</span>
                      <span>{invoiceDetail.net_total?.toFixed(2)}€</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">MwSt. ({invoiceDetail.tax_rate}%)</span>
                      <span>{invoiceDetail.tax_amount?.toFixed(2)}€</span>
                    </div>
                    <div className="flex justify-between font-bold text-lg border-t pt-2">
                      <span>Gesamtbetrag</span>
                      <span>{invoiceDetail.total?.toFixed(2)}€</span>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Payment Info */}
              <div className="bg-blue-50 rounded-lg p-4 text-sm">
                <p className="font-medium text-blue-900 mb-2">Zahlungshinweise</p>
                <p className="text-blue-800">
                  Wir senden Ihnen die Rechnung auf Ihre hinterlegte E-Mail und in Ihren Posteingang hier im Portal.
                </p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Print Styles */}
      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          #invoice-print-area, #invoice-print-area * {
            visibility: visible;
          }
          #invoice-print-area {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            padding: 20px;
          }
          .print\\:hidden {
            display: none !important;
          }
          @page {
            margin: 1cm;
          }
        }
      `}</style>
    </div>
  );
}
